import { useConfig } from "$global-providers/config-provider/provider";
import { useDevTools } from "$global-providers/dev-tools-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { isJestEnvironment } from "$routes/_lib/environment";
import { getUserLanguage } from "$routes/_lib/use-persist-user-language";
import {
  type ApolloCache,
  ApolloClient,
  type ApolloError,
  ApolloLink,
  type ApolloQueryResult,
  ApolloProvider as DefaultProvider,
  HttpLink,
  type NormalizedCacheObject,
  Observable,
  type Operation,
} from "@apollo/client";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { useAuth0 } from "@auth0/auth0-react";
import { sha256 } from "js-sha256";
import { is } from "ramda";
import { useMemo } from "react";
import { RetryApolloProvider } from "./retry-apollo-provider";
import { ElephantRetryLink } from "./retry-link";
import { newCache } from "./utils";

export const ApolloProvider: FCwC = ({ children }) => {
  const { federationPath } = useConfig();
  const authLink = useAuthLink();
  const { setTime } = useServerTime();
  const cache = useMemo(() => newCache(), []);
  const [apqEnabled] = useFeatureFlag(featureFlags.apqEnabled);
  const { devToolsEnabled } = useDevTools();

  const client = useMemo(
    () =>
      buildApolloClient({
        federationPath: federationPath,
        cache,
        authLink,
        setTime,
        apqEnabled,
        devToolsEnabled,
      }),
    [federationPath, cache, authLink, setTime, apqEnabled, devToolsEnabled],
  );
  const retryClient = useMemo(
    () =>
      buildApolloClient({
        federationPath: federationPath,
        cache,
        authLink,
        retryLink,
        setTime,
        apqEnabled,
        devToolsEnabled,
      }),
    [federationPath, cache, authLink, setTime, apqEnabled, devToolsEnabled],
  );

  return (
    <DefaultProvider client={client}>
      <RetryApolloProvider client={retryClient}>{children}</RetryApolloProvider>
    </DefaultProvider>
  );
};

const buildApolloClient = ({
  federationPath,
  authLink,
  retryLink,
  setTime,
  cache,
  apqEnabled,
  devToolsEnabled,
}: {
  federationPath: string;
  authLink: ApolloLink;
  retryLink?: ApolloLink;
  setTime: (time: Date) => void;
  cache: ApolloCache<NormalizedCacheObject>;
  apqEnabled?: boolean;
  devToolsEnabled: boolean;
}) =>
  new ApolloClient({
    name: SERVICE_NAME,
    version: APP_VERSION_SEMANTIC,
    connectToDevTools: devToolsEnabled,
    cache,
    link: ApolloLink.from(
      [
        removeTypenameFromInputLink,
        addLocationMiddleware,
        serverTimeMiddleware(setTime),
        authLink,
        retryLink,
        ...(!apqEnabled || isJestEnvironment() ? [] : [apqMiddleware]), // APQs don't play nice with msw
        new HttpLink({ uri: federationPath }),
      ].filter(Boolean),
    ),
  });

export const omitTypename = (
  variables: Record<string, unknown> | Array<Record<string, unknown>> | unknown,
) => {
  const fieldsToOmit = ["__typename", "|typename"];
  if (is(Array)(variables)) {
    return (variables as Array<unknown>).map(omitTypename);
  }

  if (is(Object)(variables)) {
    return Object.entries(variables).reduce(
      (acc: Record<string, unknown>, [key, value]: [string, unknown]) => {
        if (!fieldsToOmit.includes(key)) {
          acc[key] = omitTypename(value);
        }
        return acc;
      },
      {},
    );
  }

  return variables;
};

export const removeTypenameFromInputLink = new ApolloLink(
  (operation, forward) => {
    if (operation.variables) {
      operation.variables = omitTypename(operation.variables);
    }

    return forward(operation);
  },
);

const addLocationMiddleware = new ApolloLink((operation, forward) => {
  const customHeaders = operation.getContext().headers ?? {};
  const headers: { [k: string]: string } = {
    "X-Elephant-Locale": getUserLanguage(),
    ...customHeaders,
  };
  operation.setContext({
    headers,
  });

  return forward(operation);
});

// https://www.apollographql.com/docs/apollo-server/performance/apq/
const apqMiddleware = createPersistedQueryLink({ sha256 });

const retryLink = new ElephantRetryLink({
  // Increase delay times from 1s to 5s max between retries.
  // To avoid Thundering herd randomise delay times from 0 to 5s max
  calculateDelay: (count: number) => {
    return (count > 5 ? 5 : count) * 1000 * Math.random();
  },
  // Retry if its a query and it has errors, either graphql generated from a
  // network error or network occured at the proxy level.
  // NOTE: Federation returns graphql errors if the one of the dependent services for the query is down.
  shouldRetry: (
    _count: number, // retry number
    operation: Operation,
    value: ApolloError | ApolloQueryResult<unknown>, // response from server
    proxyError: Error, // error from proxy
  ) => {
    const isQuery = operation.query.definitions.reduce((acc, def) => {
      if (def.kind === "OperationDefinition") {
        return acc && (!def.operation || def.operation === "query");
      }
      return acc;
    }, true);

    const maybeResponse = value as ApolloQueryResult<unknown>;

    // The proxy is up but the serving server is down.
    const hostNetError = maybeResponse?.errors?.find((err) =>
      err.message.includes("ECONNREFUSED"),
    );

    const retry = isQuery && (!!hostNetError || !!proxyError);

    if (retry) {
      // useful for testing, but also to allow client logic to track retries
      const onRetryHandler = operation.getContext().onRetry;
      if (onRetryHandler) {
        onRetryHandler();
      }
    }
    return retry;
  },
});

const PLAUSIBLE_SERVER_TIME_FLOOR = new Date("2020-01-01T00:00:00.000Z");
const PLAUSIBLE_SERVER_TIME_CEILING = new Date("2100-01-01T00:00:00.000Z");

/**
 * A missing header yields the epoch and an unparseable one yields `Invalid
 * Date`; adopting either as the offset pins every offline timestamp to 1970.
 */
export const isPlausibleServerTime = (serverTime: Date) =>
  serverTime >= PLAUSIBLE_SERVER_TIME_FLOOR &&
  serverTime <= PLAUSIBLE_SERVER_TIME_CEILING;

/**
 * Maintains the last known server time according to the header set by api-federation.
 * @param setTime - A function that will receive the latest time each time an apollo fetch occurs.
 * @returns
 */
const serverTimeMiddleware = (setTime: (time: Date) => void) =>
  new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext();
      const dateHeader = context.response.headers.get("Date");
      if (!dateHeader) return response;
      const serverTime = new Date(dateHeader);
      if (isPlausibleServerTime(serverTime)) {
        setTime(serverTime);
      }
      return response;
    });
  });

const useAuthLink = () => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();

  const authLink = new ApolloLink((operation, forward) => {
    return new Observable((observer) => {
      if (!isLoading && isAuthenticated) {
        getAccessTokenSilently()
          .then((token) => {
            operation.setContext({
              headers: {
                ...operation.getContext().headers,
                authorization: token,
              },
            });
            return forward(operation);
          })
          .then((observable) => {
            observable.subscribe(observer);
          })
          .catch((error) => {
            observer.error(error);
          });
      }
    });
  });

  return authLink;
};
